import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { TierPricingSchema } from '@vendor/types';
import type { TierPricing } from '@vendor/types';
import { supabase } from '../supabase';
import { VENDOR_TIER_IDS } from '../tiers';

/**
 * Fetches the live monthly price of the Vendor Pro plan from `public.tiers`.
 *
 * Shell: any — consumed by the public `/subscribe` page and the desktop
 * lapsed-account upsell, both of which quote a price before the account has
 * an active subscription.
 *
 * Non-obvious contracts:
 *   - `public.tiers.monthly_price_cents` is the single source of truth for
 *     this price (vendor#156 — four call sites each carried their own literal
 *     and disagreed with each other and with Stripe). Never reintroduce a
 *     dollar literal alongside this hook.
 *   - Always reads the `vendor_pro` row, never the caller's *current* tier —
 *     an account still on `vendor_trial` (whose `monthly_price_cents` is `0`)
 *     is exactly who this price is quoted to.
 *   - `tiers` is a globally readable reference table (CLAUDE.md guardrail #1;
 *     `accountScopingGuard.test.ts` `SAFE_TABLES`): no `account_id` column, no
 *     account-scoped predicate to add. Its RLS policy
 *     (`tiers_read_all_authenticated`, `TO authenticated USING (true)`) does
 *     not depend on subscription status, so a lapsed or still-trialing
 *     account can read it — this hook does not require `currentAccountId`.
 *   - Response is Zod-validated via {@link TierPricingSchema} — no raw casts
 *     on the PostgREST result.
 */
export function usePlanPrice(): UseQueryResult<TierPricing, Error> {
  return useQuery({
    queryKey: ['tier-price', VENDOR_TIER_IDS.PRO],
    queryFn: async (): Promise<TierPricing> => {
      const { data, error } = await supabase
        .from('tiers')
        .select('id, monthly_price_cents')
        .eq('id', VENDOR_TIER_IDS.PRO)
        .single();
      if (error) throw error;
      return TierPricingSchema.parse(data);
    },
  });
}
